import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { trophiesService, type ClassOwner } from '../../services/trophies';
import CategoryStrategyCard from '../components/CategoryStrategyCard';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import type { TrophyCategory } from '../../types/trophy';

/**
 * The circuit's categories — Junior, Senior, Master.
 *
 * They live on the trophy rather than on each stage because an athlete is
 * Junior for the season, not re-sorted every weekend: a roster row carries one,
 * and every registration made from it starts in the right class.
 *
 * Deleting one does not delete the athletes in it. They keep their place on the
 * books and simply have no class until somebody says otherwise — which is why
 * the confirmation says how many people that will be.
 */
export default function CategoriesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location: trophy, loading: trophyLoading, slug, locationType } = useCurrentLocation();
  // A gym's own event keeps its own list, on the same endpoints under its
  // own path; the menu never shows this page on a stage.
  const owner: ClassOwner = locationType === 'competition' ? 'competition' : 'trophy';

  const [categories, setCategories] = useState<TrophyCategory[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(
    async (trophySlug: string) => {
      setListLoading(true);
      try {
        setCategories(await trophiesService.listCategories(trophySlug, owner));
      } catch (error) {
        toast({
          title: t('gymAdmin.categories.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast, owner]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const reportFailure = (error: unknown) =>
    toast({
      title: t('gymAdmin.categories.saveError'),
      description: error instanceof Error ? error.message : t('errors.unexpectedError'),
      variant: 'destructive',
    });

  const handleCreate = async () => {
    if (!slug || !newName.trim()) return;
    setSaving(true);
    try {
      const created = await trophiesService.createCategory(slug, newName.trim(), owner);
      setCategories((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
    } catch (error) {
      reportFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (category: TrophyCategory) => {
    if (!slug || !editingName.trim()) return;
    setBusyId(category.id);
    try {
      const updated = await trophiesService.updateCategory(slug, category.id, editingName.trim(), owner);
      setCategories((current) =>
        current
          .map((c) => (c.id === updated.id ? updated : c))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingId(null);
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (category: TrophyCategory) => {
    if (!slug) return;
    const message =
      category.entries_count > 0
        ? t('gymAdmin.categories.confirmDeleteWithMembers', {
            name: category.name,
            count: category.entries_count,
          })
        : t('gymAdmin.categories.confirmDelete', { name: category.name });
    if (!window.confirm(message)) return;

    setBusyId(category.id);
    try {
      await trophiesService.deleteCategory(slug, category.id, owner);
      setCategories((current) => current.filter((c) => c.id !== category.id));
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusyId(null);
    }
  };

  if (trophyLoading) return <Skeleton className="h-96 w-full max-w-3xl" />;
  if (!trophy || !slug) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.categories.title')}</CardTitle>
          <CardDescription>{t('gymAdmin.categories.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {listLoading && <Skeleton className="h-24 w-full" />}

          {!listLoading && categories.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('gymAdmin.categories.empty')}
            </p>
          )}

          {!listLoading &&
            categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center gap-3 rounded-lg border p-3"
                data-testid="category-row"
              >
                {editingId === category.id ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(category)}
                      aria-label={t('gymAdmin.categories.name')}
                      autoFocus
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRename(category)}
                      disabled={busyId === category.id}
                      title={t('common.save')}
                    >
                      {busyId === category.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditingId(null)}
                      title={t('common.cancel')}
                    >
                      <X className="size-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate font-medium">{category.name}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="size-3" aria-hidden />
                      {category.entries_count}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(category.id);
                        setEditingName(category.name);
                      }}
                      title={t('gymAdmin.categories.rename')}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(category)}
                      disabled={busyId === category.id}
                      title={t('gymAdmin.categories.delete')}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}

          <div className="flex gap-2 pt-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder={t('gymAdmin.categories.namePlaceholder')}
              aria-label={t('gymAdmin.categories.name')}
            />
            <Button onClick={handleCreate} disabled={!newName.trim() || saving} className="gap-2">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t('gymAdmin.categories.add')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* After the list has landed: the table is matched against it, and drawn
          any earlier it would be read twice. */}
      {!listLoading && (
        <CategoryStrategyCard
          slug={slug}
          owner={owner}
          categories={categories}
          onCategoriesCreated={(created) =>
            setCategories((current) => [...current, ...created].sort((a, b) => a.name.localeCompare(b.name)))
          }
        />
      )}
    </div>
  );
}
